<template>
  <div class="grid-view">
    <div class="main-content">
      <search-item config-module="231504" :show-simple="false" :expand-adv="true" :custom-search-items="customSearchItems" no="1" @advSearchItemCallBack="toAdvancedQuery">
        <template slot="operations">
          <el-button :disabled="!selectedNode" type="primary" no="2" @click="toLook()">{{ $t('CargoMsg.Cargo_View') }}</el-button>
          <el-button v-permission="['role_CRMRecoveryCustomerManager_recovery']" type="primary" no="3" @click="toRecovery">{{ $t('CargoMsg.Cargo_Restore') }}</el-button>
        </template>
      </search-item>
      <div class="section grid-wrapper">
        <cargo-ag-grid
          config-module="23150102"
          :grid-options="gridOptions"
          :column-defs="columnDefs"
          :row-data="rowData"
          no="4"
          @gridReady="params=>{gridApi=params.api}"
        />
      </div>
      <div class="pagination-wrapper">
        <cargo-pagination
          config-module="23150102"
          :page.sync="filter.pageNum"
          :limit.sync="filter.pageSize"
          :total="total"
          no="5"
          @pagination="toAdvancedQuery()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import searchItem from '@/components/Cargo/Common/cargoCombinationSearch'
import ColumnTagRenderer from '@/components/Cargo/Common/ColumnTagRenderer'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import ColumnUtils from '@/utils/ColumnUtils'
import { getLoading4Cxt } from '@/utils/utilBaseElement'
import AdvancedSearchOption from '@/class/cargo/model/parameters/AdvancedSearchOption'
import OperatorConstants from '@/class/cargo/model/parameters/OperatorConstants'
import { queryQuotationClientPageable, updateQuotationClientField } from '@/api/sales/quotationClient'
import AdvancedSearchField from '@/class/cargo/model/parameters/AdvancedSearchField'
import { OPERATOR_IN } from '@/class/cargo/model/utils/AdvancedSearchConsts'

export default {
  name: 'CustomerRecoveryTreeGrid',
  components: { searchItem },
  data() {
    const defaultSearchFields = []

    // 所属城市
    let searchField = new AdvancedSearchField()
    searchField.action = AdvancedSearchField.UPDATE_ACTION
    searchField.itemType = 'countryAreaCascader'
    searchField.itemCondition = [OPERATOR_IN]
    searchField.defaultConditionVal = OPERATOR_IN.code
    searchField.fieldType = 'FIELD_TEXT'
    searchField.itemName = 'quotationClient.PROVINCE'
    searchField.itemLabel = 'CargoMsg.Cargo_City'
    searchField.defaultValue = []
    defaultSearchFields.push(searchField.formatObj2Json())

    // CARGO-45606 CRM模块中，增加合作伙伴简称字段
    searchField = new AdvancedSearchField()
    searchField.action = AdvancedSearchField.UPDATE_ACTION
    searchField.itemType = 'input'
    searchField.fieldType = 'FIELD_TEXT'
    searchField.itemName = 'quotationClient.SHORT_NAME'
    searchField.itemLabel = 'CargoMsg.Cargo_Abbreviation'
    searchField.defaultValue = []
    defaultSearchFields.push(searchField.formatObj2Json())

    const customSearchItems = defaultSearchFields

    return {
      customSearchItems,
      filter: {
        conditions: [
          new AdvancedSearchOption(null, 'quotationClient.ACTIVE', 0).formatObj2Json()
        ],
        roleMap: {},
        branchId: 0,
        pageNum: 1,
        pageSize: 50
      },
      total: 0,
      gridOptions: {
        frameworkComponents: {
          ColumnTagRenderer
        },
        rowSelection: 'multiple',
        suppressCellSelection: true,
        suppressRowClickSelection: true,
        onCellFocused: event => {
          const nodes = event.api.rowModel.rowsToDisplay
          this.selectedNode = nodes[event.rowIndex] || null
        },
        onCellDoubleClicked: event => {
          this.toLook(event.data)
        },
        onSortChanged: event => {
          if (this.selectedNode) {
            event.api.setFocusedCell(this.selectedNode.rowIndex, event.columnApi.getAllDisplayedColumns()[0])
          }
        },
        context: {
          componentParent: this
        }
      },
      columnDefs: [
        {
          headerName: '',
          checkboxSelection: true,
          width: 55,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false,
          headerCheckboxSelection: true
        }, {
          headerName: this.$t('CargoMsg.Cargo_ClientNo'),
          field: 'clientId',
          colId: 'clientId',
          valueFormatter: params => {
            if (!params.value) return '0'
            return params.value.toString()
          },
          width: 120
        }, {
          headerName: this.$t('CargoMsg.Cargo_ChineseName'),
          field: 'name',
          colId: 'name',
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_EnglishName'),
          field: 'englishName',
          colId: 'englishName',
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_BranchCompany'),
          field: 'branchId',
          colId: 'branchId',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Office_All, 'officeId', params.value, 'code')
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_ScaleOfCompanyEmployees'),
          field: 'companySize',
          colId: 'companySize',
          valueFormatter: params => {
            if (!params.value) return ''
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_RegisteredCapitalIn10Thousand'),
          field: 'registeredCapital',
          colId: 'registeredCapital',
          valueFormatter: params => {
            if (!params.value) return ''
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_CreatedDate'),
          field: 'dateCreated',
          colId: 'dateCreated',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.dateLabelFunc(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_ClientStatus'),
          field: 'customerStatusCode',
          colId: 'customerStatusCode',
          cellRendererSelector: params => {
            let color = ''
            switch (params.value) {
              case '0': // 潜在阶段
                color = '#8DCE64'
                break
              case '1': // 意向阶段
                color = '#00A795'
                break
              case '2': // 商务阶段
                color = '#00A6F4'
                break
              case '3': // 合作阶段
                color = '#5469BA'
                break
            }
            return {
              component: 'ColumnTagRenderer',
              params: {
                color,
                value: ColumnUtils.customerStatusCodeToColumnLabelFun(params.value)
              }
            }
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_CustomerType'),
          field: 'companyTypeCode',
          colId: 'companyTypeCode',
          valueFormatter: params => {
            return ColumnUtils.companyTypeCodeToColumnLabelFun(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_SourceOfClient'),
          field: 'sourceType',
          colId: 'sourceType',
          hidden: !this.showSourceType,
          valueFormatter: params => {
            return ColumnUtils.sourceTypeCodeToColumnLabelFun(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_FirstContactDate'),
          field: 'firstMeetDate',
          colId: 'firstMeetDate',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.dateLabelFunc(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_Competitor'),
          field: 'competitorCompany',
          colId: 'competitorCompany',
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_Sales'),
          field: 'ownerId',
          colId: 'ownerId',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_User, 'userId', params.value, 'chineseName')
          },
          width: 100
        }, {
          headerName: this.$t('CargoMsg.Cargo_30DaysShpt'),
          field: 'freightVolume30',
          colId: 'freightVolume30',
          valueFormatter: params => {
            if (!params.value) return '0'
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_60DaysShpt'),
          field: 'freightVolume60',
          colId: 'freightVolume60',
          valueFormatter: params => {
            if (!params.value) return '0'
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_90DaysShpt'),
          field: 'freightVolume90',
          colId: 'freightVolume90',
          valueFormatter: params => {
            if (!params.value) return '0'
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_Over90DaysShpt'),
          field: 'freightVolumeGt90',
          colId: 'freightVolumeGt90',
          valueFormatter: params => {
            if (!params.value) return '0'
            return params.value.toString()
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_DaysNotFollowedUp'),
          field: 'unAskDays',
          colId: 'unAskDays',
          hidden: true,
          valueFormatter: params => {
            if (!params.value) return '0'
            return ColumnUtils.formatUnAskDays(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_PreferredShippingRoute'),
          field: 'airlineCode',
          colId: 'airlineCode',
          cellRenderer: (params) => {
            if (!params.value) return ''
            return ColumnUtils.likeAirlineCode(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_PreferredPort'),
          field: 'seaPortCode',
          colId: 'seaPortCode',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.likeSeaPortCode(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_PreferredAirport'),
          field: 'airPortCode',
          colId: 'airPortCode',
          valueFormatter: params => {
            if (!params.value) return ''
            return ColumnUtils.likeAirPortCode(params.value)
          },
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_Remarks'),
          field: 'remarks',
          colId: 'remarks',
          width: 150
        }, {
          headerName: this.$t('CargoMsg.Cargo_CustomerContactChannel'),
          field: 'customerContactChannel',
          colId: 'customerContactChannel',
          hide: true,
          cellRenderer: (params) => {
            return ColumnUtils.customerContactChannelLabelFun(params.value)
          },
          width: 140
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Country'),
          field: 'country',
          width: 150,
          cellRenderer: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Country, 'code', params.value, this.isZh ? 'chineseName' : 'name')
          }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_Abbreviation'),
          field: 'shortName',
          width: 150,
          hide: true
        }
      ],
      rowData: [],
      selectedNode: null,
      gridApi: null
    }
  },
  computed: {
    isZh() {
      return this.$store.getters.language === 'zh'
    }
  },
  methods: {
    toAdvancedQuery(conditions) {
      if (conditions) {
        let option = AdvancedSearchOption.removeOption('quotationClient.ASSIGN_DATE', conditions)
        if (option) {
          if (option.value === 1) {
            conditions.push(new AdvancedSearchOption(null, 'quotationClient.ASSIGN_DATE', 1, OperatorConstants.OPERATOR_IS_NOT_NULL.code).formatObj2Json())
          } else if (option.value === 0) {
            conditions.push(new AdvancedSearchOption(null, 'quotationClient.ASSIGN_DATE', 0, OperatorConstants.OPERATOR_IS_NULL.code).formatObj2Json())
          }
        }
        option = AdvancedSearchOption.removeOption('quotationClientBusinessMultipleDetail.airlineCode', conditions)
        if (option) {
          option.value = String(option.value)
          conditions.push(option)
        }
        option = AdvancedSearchOption.removeOption('quotationClientBusinessMultipleDetail.seaPortCode', conditions)
        if (option) {
          option.value = String(option.value)
          conditions.push(option)
        }
        option = AdvancedSearchOption.removeOption('quotationClientBusinessMultipleDetail.airPortCode', conditions)
        if (option) {
          option.value = String(option.value)
          conditions.push(option)
        }
        // 删除的客户ACTIVE=0，新增时默认为1
        conditions.push(new AdvancedSearchOption(null, 'quotationClient.ACTIVE', 0).formatObj2Json())
        /* 即将到期天数 */
        const roleMap = {}
        if (AdvancedSearchOption.hasField('quotationClient.DUE_TIME', conditions)) {
          const option = AdvancedSearchOption.removeOption('quotationClient.DUE_TIME', conditions)
          roleMap.dueTime = { 'value': option.value, 'operator': option.operator }
        }
        // 所属城市
        if (AdvancedSearchOption.hasField('quotationClient.PROVINCE', conditions)) {
          const option = AdvancedSearchOption.removeOption('quotationClient.PROVINCE', conditions)
          const countryArr = []
          const provinceArr = []
          const cityArr = []
          const districtsArr = []
          for (const item of option.value) {
            switch (item.length) {
              case 1:
                countryArr.push(item[0])
                break
              case 2:
                provinceArr.push(item[1])
                break
              case 3:
                cityArr.push(item[2])
                break
              case 4:
                districtsArr.push(item[3])
                break
            }
          }
          if (countryArr.length > 0) {
            conditions.push({ ...option, fieldName: 'quotationClient.COUNTRY', value: countryArr })
          }
          if (provinceArr.length > 0) {
            conditions.push({ ...option, fieldName: 'quotationClient.PROVINCE', value: provinceArr })
          }
          if (cityArr.length > 0) {
            conditions.push({ ...option, fieldName: 'quotationClient.CITY', value: cityArr })
          }
          if (districtsArr.length > 0) {
            conditions.push({ ...option, fieldName: 'quotationClient.DISTRICTS', value: districtsArr })
          }
        }
        this.filter.conditions = conditions
        this.filter.roleMap = roleMap
        this.filter.branchId = this.$store.getters.currentOfficeId
        this.filter.pageNum = 1
      }
      const loading = getLoading4Cxt(this)
      queryQuotationClientPageable(this.filter).then(res => {
        const data = res.data
        this.rowData = data.list
        this.filter.pageNum = data.pageNum
        this.filter.pageSize = data.pageSize
        this.total = Number(data.total)
        this.gridApi.clearFocusedCell()
      }).finally(() => {
        loading.close()
      })
    },
    toRemove(removedUuids) {
      // 异步执行，避免页面卡顿
      setTimeout(() => {
        for (let i = this.rowData.length - 1; i >= 0; --i) {
          if (removedUuids.includes(this.rowData[i].uuid)) {
            this.rowData.splice(i, 1)
          }
        }
        /* 记录单选项 */
        if (this.selectedNode) {
          const selectedUuid = this.selectedNode.data.uuid
          if (removedUuids.includes(selectedUuid)) {
            this.gridApi.clearFocusedCell()
          } else {
            setTimeout(() => {
              const nodes = this.gridApi.rowModel.rowsToDisplay
              const node = nodes.find(p => p.data.uuid === selectedUuid)
              if (node) {
                this.gridApi.setFocusedCell(node.rowIndex, this.gridApi.columnController.getAllDisplayedColumns()[0])
              }
            }, 100)
          }
        }
        /* 记录勾选项 */
        const rows = this.gridApi.getSelectedRows()
        if (rows.length > 0) {
          const uuids = Array.from(rows, p => p.uuid)
          setTimeout(() => {
            const nodes = this.gridApi.rowModel.rowsToDisplay
            for (const node of nodes) {
              if (uuids.includes(node.data.uuid)) {
                node.setSelected(true)
              }
            }
          }, 100)
        }
      })
    },
    toLook(row) {
      this.$emit('look', row || this.selectedNode.data)
    },
    toRecovery() {
      const rows = this.gridApi.getSelectedRows()
      if (rows.length === 0) {
        this.$message.warning(this.$t('CargoMsg.Cargo_PleaseCheckClient'))
        return
      }
      this.$confirm(this.$t('CargoMsg.Cargo_RestoreSelectedClient'), this.$t('CargoMsg.Cargo_ConfirmNotification'), {
        confirmButtonText: this.$t('constant.yes'),
        cancelButtonText: this.$t('constant.no'),
        type: 'warning',
        closeOnClickModal: false
      }).then(() => {
        const loading = getLoading4Cxt(this)
        updateQuotationClientField({
          conMap: { active: true },
          clientKeys: Array.from(rows, p => p.uuid),
          branchId: this.$store.getters.currentOfficeId
        }).then(res => {
          this.filter.pageNum = 1
          this.toAdvancedQuery()
        }).finally(() => {
          loading.close()
        })
      }).catch(() => { })
    }
  }
}
</script>
